import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import {
  accountsApi,
  chartApi,
  costCentersApi,
  partiesApi,
  receivablesApi,
  type Receivable,
  type ReceivableStatus,
  type ReceivablesSummary,
} from '@/api';
import { MonthPeriodFilter } from '@/components/ui/MonthPeriodFilter';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { currentMonthRange, formatBRL, type DateRange } from '@/lib/utils';
import { ApiError } from '@/lib/api';

const colors: Record<ReceivableStatus, 'success' | 'warning' | 'danger' | 'default'> = {
  RECEIVED: 'success', PARTIAL: 'warning', OVERDUE: 'danger', OPEN: 'default', CANCELED: 'default',
};
const labels: Record<ReceivableStatus, string> = {
  RECEIVED: 'Recebido', PARTIAL: 'Parcial', OVERDUE: 'Vencido', OPEN: 'Em aberto', CANCELED: 'Cancelado',
};

const EDITABLE: ReceivableStatus[] = ['OPEN', 'PARTIAL', 'OVERDUE'];

// Vencidos primeiro, depois em aberto/parcial, depois recebidos e cancelados.
const statusOrder: Record<ReceivableStatus, number> = {
  OVERDUE: 0, PARTIAL: 1, OPEN: 2, RECEIVED: 3, CANCELED: 4,
};

type ReceivableSortColumn =
  | 'dueDate'
  | 'party'
  | 'description'
  | 'chart'
  | 'amount'
  | 'receivedAmount'
  | 'status';

export function ContasReceberPage() {
  const [status, setStatus] = useState<ReceivableStatus | ''>('');
  const [period, setPeriod] = useState<DateRange>(() => currentMonthRange());
  const [sort, setSort] = useState<SortState<ReceivableSortColumn>>({ column: 'dueDate', dir: 'asc' });
  const toggleSort = makeToggleSort(setSort);
  const [recDialog, setRecDialog] = useState<Receivable | null>(null);
  const [formDialog, setFormDialog] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; receivable: Receivable }
    | { mode: 'clone'; receivable: Receivable }
    | null
  >(null);

  const { data, isLoading } = useQuery({
    queryKey: ['receivables', status, period],
    queryFn: () => receivablesApi.list({
      ...(status ? { status } : {}),
      from: period.from,
      to: period.to,
    }),
  });

  const { data: summary } = useQuery({
    queryKey: ['receivables-summary', period],
    queryFn: () => receivablesApi.summary({
      from: period.from,
      to: period.to,
    }),
  });

  const qc = useQueryClient();

  const remove = useMutation({
    mutationFn: (id: string) => receivablesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receivables'] });
      qc.invalidateQueries({ queryKey: ['receivables-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err) => {
      alert(err instanceof ApiError ? err.message : 'Erro ao excluir lançamento.');
    },
  });

  function handleDelete(r: Receivable) {
    const ok = window.confirm(
      `Excluir o lançamento "${r.description}" de ${r.party.name}?\n\n` +
      `Esta ação é permanente. Para títulos já recebidos, use "Cancelar" no menu de edição.`,
    );
    if (ok) remove.mutate(r.id);
  }

  // Linhas ordenadas pela coluna escolhida; o filtro vem do back.
  const sortedItems = useMemo(() => {
    const rows = data?.items ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'dueDate':        primary = cmp.date(a.dueDate, b.dueDate); break;
        case 'party':          primary = cmp.text(a.party.name, b.party.name); break;
        case 'description':    primary = cmp.text(a.description, b.description); break;
        case 'chart':          primary = cmp.text(a.chartOfAccount.name, b.chartOfAccount.name); break;
        case 'amount':         primary = cmp.number(a.amount, b.amount); break;
        case 'receivedAmount': primary = cmp.number(a.receivedAmount, b.receivedAmount); break;
        case 'status':         primary = statusOrder[a.status] - statusOrder[b.status]; break;
      }
      if (primary !== 0) return primary * factor;
      const dueCmp = cmp.date(a.dueDate, b.dueDate);
      if (dueCmp !== 0) return dueCmp;
      return cmp.text(a.description, b.description);
    });
  }, [data, sort]);

  return (
    <>
      <PageHeader
        title="Contas a receber"
        breadcrumb="Compromissos"
        actions={
          <Button size="sm" onClick={() => setFormDialog({ mode: 'create' })}>
            <Plus size={14} className="mr-1" /> Novo lançamento
          </Button>
        }
      />
      <PageBody>
        <MonthPeriodFilter value={period} onChange={setPeriod} />

        <SummaryCards summary={summary} />

        <div className="bg-card border rounded-lg p-3 flex flex-wrap gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={status} onChange={(e) => setStatus(e.target.value as ReceivableStatus | '')}>
              <option value="">Todos</option>
              <option value="OPEN">Em aberto</option>
              <option value="PARTIAL">Parcial</option>
              <option value="OVERDUE">Vencido</option>
              <option value="RECEIVED">Recebido</option>
              <option value="CANCELED">Cancelado</option>
            </Select>
          </div>
        </div>

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="dueDate"        label="Vencimento" sort={sort} onClick={toggleSort} />
                <SortHeader column="party"          label="Cliente"    sort={sort} onClick={toggleSort} />
                <SortHeader column="description"    label="Descrição"  sort={sort} onClick={toggleSort} />
                <SortHeader column="chart"          label="Categoria"  sort={sort} onClick={toggleSort} />
                <SortHeader column="amount"         label="Valor"      sort={sort} onClick={toggleSort} align="right" />
                <SortHeader column="receivedAmount" label="Recebido"   sort={sort} onClick={toggleSort} align="right" />
                <SortHeader column="status"         label="Status"     sort={sort} onClick={toggleSort} />
                <th />
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Carregando…</td></tr>}
              {sortedItems.map((r) => {
                const editable = EDITABLE.includes(r.status);
                const receivable = r.status === 'OPEN' || r.status === 'PARTIAL' || r.status === 'OVERDUE';
                return (
                  <tr key={r.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(r.dueDate).toLocaleDateString('pt-BR')}</td>
                    <td className="px-3 py-2">{r.party.name}</td>
                    <td className="px-3 py-2">{r.description}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.chartOfAccount.name}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatBRL(r.amount)}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">
                      {Number(r.receivedAmount) > 0 ? formatBRL(r.receivedAmount) : '—'}
                    </td>
                    <td className="px-3 py-2"><Badge variant={colors[r.status]}>{labels[r.status]}</Badge></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        {editable && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setFormDialog({ mode: 'edit', receivable: r })}
                            aria-label="Editar"
                            title="Editar lançamento"
                          >
                            <Pencil size={14} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setFormDialog({ mode: 'clone', receivable: r })}
                          aria-label="Clonar"
                          title="Clonar lançamento"
                        >
                          <Copy size={14} />
                        </Button>
                        {receivable && (
                          <Button size="sm" variant="outline" onClick={() => setRecDialog(r)}>Receber</Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(r)}
                          disabled={remove.isPending}
                          aria-label="Excluir"
                          title="Excluir lançamento"
                        >
                          <Trash2 size={14} className="text-rose-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!isLoading && (data?.items.length ?? 0) === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Nenhuma conta a receber</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {data && (
          <div className="text-sm text-muted-foreground">{data.items.length} de {data.total} títulos</div>
        )}
      </PageBody>

      {recDialog && <ReceiveDialog receivable={recDialog} onClose={() => setRecDialog(null)} />}
      {formDialog && (
        <ReceivableFormDialog
          mode={formDialog.mode}
          receivable={formDialog.mode !== 'create' ? formDialog.receivable : undefined}
          onClose={() => setFormDialog(null)}
        />
      )}
    </>
  );
}

// =====================================================================
// Form: Novo lançamento ou edição
// =====================================================================
interface ReceivableFormDialogProps {
  mode: 'create' | 'edit' | 'clone';
  receivable?: Receivable;
  onClose: () => void;
}

function ReceivableFormDialog({ mode, receivable, onClose }: ReceivableFormDialogProps) {
  const qc = useQueryClient();
  const isEdit = mode === 'edit';
  const isClone = mode === 'clone';
  // Em edit usa o receivable original; em clone, pré-preenche os campos do
  // template mas vai criar como novo lançamento.
  const seed = isEdit || isClone ? receivable : undefined;

  const today = new Date().toISOString().slice(0, 10);
  const [partyId, setPartyId] = useState(seed?.party.id ?? '');
  const [chartOfAccountId, setChartOfAccountId] = useState(seed?.chartOfAccount.id ?? '');
  const [costCenterId, setCostCenterId] = useState(seed?.costCenter?.id ?? '');
  const [description, setDescription] = useState(seed?.description ?? '');
  // Reference (NF/recibo) é único do lançamento original — limpa ao clonar.
  const [reference, setReference] = useState(isClone ? '' : (seed?.reference ?? ''));
  const [amount, setAmount] = useState(seed ? Number(seed.amount).toFixed(2) : '');
  // Datas: em edit mantém; em clone reseta para hoje (template para nova data).
  const [dueDate, setDueDate] = useState(isEdit && seed ? seed.dueDate.slice(0, 10) : today);
  const [issueDate, setIssueDate] = useState(isEdit && seed?.issueDate ? seed.issueDate.slice(0, 10) : today);
  const [notes, setNotes] = useState(seed?.notes ?? '');

  const [installments, setInstallments] = useState(false);
  const [count, setCount] = useState('2');
  const [startDate, setStartDate] = useState(today);

  const { data: parties } = useQuery({ queryKey: ['parties'], queryFn: () => partiesApi.list() });
  const { data: charts } = useQuery({ queryKey: ['charts'], queryFn: chartApi.list });
  const { data: ccs } = useQuery({ queryKey: ['ccs'], queryFn: costCentersApi.list });

  const customerParties = useMemo(
    () => parties?.filter((p) => p.type === 'CUSTOMER' && p.active) ?? [],
    [parties],
  );
  const revenueCharts = useMemo(
    () => charts?.filter((c) => c.nature === 'REVENUE' && c.active) ?? [],
    [charts],
  );

  const received = isEdit && receivable ? Number(receivable.receivedAmount) : 0;
  const lockAmountTooLow = isEdit && received > 0 && Number(amount) < received;

  const create = useMutation({
    mutationFn: () => {
      if (installments) {
        return receivablesApi.installments({
          partyId,
          chartOfAccountId,
          ...(costCenterId ? { costCenterId } : {}),
          description,
          ...(reference ? { reference } : {}),
          amount: Number(amount),
          count: Number(count),
          startDate,
        });
      }
      return receivablesApi.create({
        partyId,
        chartOfAccountId,
        ...(costCenterId ? { costCenterId } : {}),
        description,
        ...(reference ? { reference } : {}),
        amount: Number(amount),
        dueDate,
        issueDate,
        ...(notes ? { notes } : {}),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receivables'] });
      qc.invalidateQueries({ queryKey: ['receivables-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
  });

  const update = useMutation({
    mutationFn: () => {
      if (!receivable) throw new Error('receivable ausente em modo edit');
      const body: Record<string, unknown> = {};
      if (partyId !== receivable.party.id) body.partyId = partyId;
      if (chartOfAccountId !== receivable.chartOfAccount.id) body.chartOfAccountId = chartOfAccountId;
      if ((costCenterId || '') !== (receivable.costCenter?.id ?? '')) body.costCenterId = costCenterId || null;
      if (description !== receivable.description) body.description = description;
      if ((reference ?? '') !== (receivable.reference ?? '')) body.reference = reference;
      if (Number(amount).toFixed(2) !== Number(receivable.amount).toFixed(2)) body.amount = Number(amount);
      if (dueDate !== receivable.dueDate.slice(0, 10)) body.dueDate = dueDate;
      if (issueDate !== (receivable.issueDate?.slice(0, 10) ?? '')) body.issueDate = issueDate;
      if ((notes ?? '') !== (receivable.notes ?? '')) body.notes = notes;
      return receivablesApi.update(receivable.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receivables'] });
      qc.invalidateQueries({ queryKey: ['receivables-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
  });

  const mut = isEdit ? update : create;
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  const requiredOk =
    partyId &&
    chartOfAccountId &&
    description.trim().length > 0 &&
    amount &&
    Number(amount) > 0 &&
    (isEdit || (installments ? startDate && Number(count) >= 1 : dueDate));

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        isEdit
          ? 'Editar conta a receber'
          : isClone
            ? 'Clonar conta a receber — novo lançamento'
            : 'Novo lançamento — Conta a receber'
      }
      className="max-w-2xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!requiredOk || lockAmountTooLow) return;
          mut.mutate();
        }}
      >
        <DialogBody className="space-y-3">
          {!isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={installments}
                onChange={(e) => setInstallments(e.target.checked)}
              />
              Parcelado (gera múltiplos vencimentos mensais)
            </label>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Cliente</Label>
              <Select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
                <option value="">— selecione —</option>
                {customerParties.map((p) => (
                  <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                ))}
                {seed && !customerParties.some((p) => p.id === seed.party.id) && (
                  <option value={seed.party.id}>{seed.party.code} — {seed.party.name} (inativo)</option>
                )}
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Categoria (receita)</Label>
              <Select value={chartOfAccountId} onChange={(e) => setChartOfAccountId(e.target.value)}>
                <option value="">— selecione —</option>
                {revenueCharts.map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
                {seed && !revenueCharts.some((c) => c.id === seed.chartOfAccount.id) && (
                  <option value={seed.chartOfAccount.id}>
                    {seed.chartOfAccount.code} — {seed.chartOfAccount.name} (inativa)
                  </option>
                )}
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Descrição</Label>
            <Input
              placeholder="Ex.: Locação de apto — maio/2026"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1 col-span-1">
              <Label>{installments ? 'Valor total' : 'Valor'}</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {lockAmountTooLow && (
                <p className="text-xs text-destructive">Não pode ser menor que o já recebido ({formatBRL(received)})</p>
              )}
            </div>

            {!installments ? (
              <>
                <div className="space-y-1">
                  <Label>Vencimento</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Emissão</Label>
                  <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <Label>Nº parcelas</Label>
                  <Input
                    type="number"
                    min="1"
                    max="360"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>1º vencimento</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Centro de custo</Label>
              <Select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}>
                <option value="">—</option>
                {ccs?.map((cc) => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Documento (opcional)</Label>
              <Input
                placeholder="NF, contrato..."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={120}
              />
            </div>
          </div>

          {!installments && (
            <div className="space-y-1">
              <Label>Observações</Label>
              <Input
                placeholder="Notas internas (opcional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          )}

          {installments && Number(count) >= 1 && Number(amount) > 0 && (
            <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2">
              Cada parcela: aprox. <span className="font-medium">{formatBRL(Number(amount) / Number(count))}</span>
              {' '}— última parcela compensa o arredondamento.
            </div>
          )}

          {isEdit && receivable && received > 0 && (
            <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2">
              Já recebido: <span className="font-medium">{formatBRL(received)}</span> — alteração não afeta recebimentos já registrados.
            </div>
          )}

          {apiError && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">{apiError}</div>}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={!requiredOk || lockAmountTooLow || mut.isPending}>
            {mut.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : (installments ? 'Lançar parcelas' : 'Lançar conta')}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// =====================================================================
// Receber título (já existia)
// =====================================================================
function ReceiveDialog({ receivable, onClose }: { receivable: Receivable; onClose: () => void }) {
  const qc = useQueryClient();
  const remaining = Number(receivable.amount) - Number(receivable.receivedAmount);
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState('PIX');
  const [fees, setFees] = useState('');
  const [discount, setDiscount] = useState('');

  const { data: accounts } = useQuery({ queryKey: ['accounts'], queryFn: accountsApi.list });

  const receive = useMutation({
    mutationFn: () => receivablesApi.receive(receivable.id, {
      accountId, amount: Number(amount), date, paymentMethod,
      fees: fees ? Number(fees) : undefined,
      discount: discount ? Number(discount) : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receivables'] });
      qc.invalidateQueries({ queryKey: ['receivables-summary'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transactions-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
  });

  const apiError = receive.error instanceof ApiError ? receive.error.message : null;

  return (
    <Dialog open onClose={onClose} title="Receber título">
      <DialogBody className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div className="text-muted-foreground">Cliente</div><div>{receivable.party.name}</div>
          <div className="text-muted-foreground">Descrição</div><div>{receivable.description}</div>
          <div className="text-muted-foreground">Vencimento</div><div>{new Date(receivable.dueDate).toLocaleDateString('pt-BR')}</div>
          <div className="text-muted-foreground">Valor original</div><div>{formatBRL(receivable.amount)}</div>
          <div className="text-muted-foreground">Já recebido</div><div>{formatBRL(receivable.receivedAmount)}</div>
          <div className="text-muted-foreground font-medium">Saldo a receber</div><div className="font-medium text-emerald-700">{formatBRL(remaining)}</div>
        </div>

        <div className="space-y-2 pt-2 border-t">
          <div className="space-y-1">
            <Label>Conta de entrada</Label>
            <Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">— selecione —</option>
              {accounts?.map((a) => <option key={a.id} value={a.id}>{a.name} ({formatBRL(a.balance)})</option>)}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Data</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Valor recebido</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Juros</Label>
              <Input type="number" step="0.01" placeholder="0,00" value={fees} onChange={(e) => setFees(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Desconto</Label>
              <Input type="number" step="0.01" placeholder="0,00" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Forma</Label>
              <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="PIX">PIX</option>
                <option value="BOLETO">Boleto</option>
                <option value="TRANSFER">Transferência</option>
                <option value="CARD">Cartão</option>
                <option value="CASH">Dinheiro</option>
                <option value="OTHER">Outro</option>
              </Select>
            </div>
          </div>
        </div>

        {apiError && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">{apiError}</div>}
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button
          disabled={!accountId || !amount || receive.isPending}
          onClick={() => receive.mutate()}
        >
          {receive.isPending ? 'Processando…' : 'Confirmar recebimento'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// =====================================================================
// Cards de resumo (Vencidos / Vencem hoje / A vencer / Recebidos / Total)
// =====================================================================
interface SummaryCardsProps {
  summary?: ReceivablesSummary;
}

function SummaryCards({ summary }: SummaryCardsProps) {
  const cards: { label: string; amount: string; count: number; tone: SummaryTone }[] = [
    { label: 'Vencidos', amount: summary?.overdue.amount ?? '0', count: summary?.overdue.count ?? 0, tone: 'danger' },
    { label: 'Vencem hoje', amount: summary?.dueToday.amount ?? '0', count: summary?.dueToday.count ?? 0, tone: 'warning' },
    { label: 'A vencer', amount: summary?.upcoming.amount ?? '0', count: summary?.upcoming.count ?? 0, tone: 'muted' },
    { label: 'Recebidos', amount: summary?.received.amount ?? '0', count: summary?.received.count ?? 0, tone: 'success' },
    { label: 'Total', amount: summary?.total.amount ?? '0', count: summary?.total.count ?? 0, tone: 'dark' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {cards.map((c) => <SummaryCard key={c.label} {...c} />)}
    </div>
  );
}

type SummaryTone = 'danger' | 'warning' | 'muted' | 'success' | 'dark';

const TONES: Record<SummaryTone, { header: string; body: string; value: string }> = {
  danger:  { header: 'bg-rose-600 text-white',     body: 'bg-card border-rose-200',    value: 'text-rose-700' },
  warning: { header: 'bg-amber-500 text-white',    body: 'bg-card border-amber-200',   value: 'text-amber-700' },
  muted:   { header: 'bg-slate-500 text-white',    body: 'bg-card border-slate-200',   value: 'text-slate-700' },
  success: { header: 'bg-emerald-600 text-white',  body: 'bg-card border-emerald-200', value: 'text-emerald-700' },
  dark:    { header: 'bg-slate-900 text-white',    body: 'bg-card border-slate-300',   value: 'text-slate-900' },
};

function SummaryCard({ label, amount, count, tone }: { label: string; amount: string; count: number; tone: SummaryTone }) {
  const t = TONES[tone];
  return (
    <div className={`rounded-lg border overflow-hidden ${t.body}`}>
      <div className={`px-3 py-1.5 text-sm font-medium text-center ${t.header}`}>{label}</div>
      <div className="px-3 py-3 text-center">
        <div className={`text-xl font-medium ${t.value}`}>{formatBRL(amount)}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{count} {count === 1 ? 'título' : 'títulos'}</div>
      </div>
    </div>
  );
}
